import {Col, Form, Input, InputNumber, Modal, Row, Select, Typography} from 'antd';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';
import toast from 'react-hot-toast';

import {RqAddProductFormData} from '../../../../../reactQuery/apis';

const {Text} = Typography;

const PRIMARY       = '#4f46e5';
const PRIMARY_LIGHT = '#eef2ff';

// ─── Section Title ─────────────────────────────────────────────────────────────
const SectionTitle = ({children}) => (
  <div className={'flex items-center gap-2 mb-4'}>
    <div className={'w-1 h-5 rounded-full'} style={{background: PRIMARY}} />
    <Text className={'font-bold text-gray-700 uppercase tracking-widest text-xs'}>
      {children}
    </Text>
  </div>
);
SectionTitle.propTypes = {children: PropTypes.node.isRequired};

// ─── Component ────────────────────────────────────────────────────────────────
const AddProductScreen = ({
  isModalVisible,
  handleCancel,
  form,
  token,
  onProductCreated,
}) => {
  const addProductMutation = RqAddProductFormData();

  // Watch cgst and sgst values dynamically to auto-update igst
  const cgstValue = Form.useWatch('cgst', form);
  const sgstValue = Form.useWatch('sgst', form);

  // Automatically update igst when cgst or sgst changes
  useEffect(() => {
    const sum = Number((Number(cgstValue || 0) + Number(sgstValue || 0)).toFixed(2));
    form.setFieldsValue({igst: sum});
  }, [cgstValue, sgstValue, form]);

  const onFinish = async values => {
    try {
      const command = {
        productName: values.productName,
        status:      values.status ?? 'ACTIVE',
        eanCode:     values.eanCode ?? '',
        cgst:        Number(values.cgst ?? 0),
        sgst:        Number(values.sgst ?? 0),
        igst:        Number(values.igst ?? 0),
        tax: {
          cgst: Number(values.cgst ?? 0),
          sgst: Number(values.sgst ?? 0),
          igst: Number(values.igst ?? 0),
        },
        quantity:    values.quantity ?? 0,
      };

      const res        = await addProductMutation.mutateAsync({token, command});
      const productId = res?.productId?.id ?? res?.id ?? res?.data?.id;

      toast.success('Product created successfully!');

      if (productId && onProductCreated) {
        onProductCreated({id: productId, productName: values.productName});
      }

      form.resetFields();
      handleCancel();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to create product');
    }
  };

  const isLoading = addProductMutation.isPending || addProductMutation.isLoading;

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-3 py-1'}>
          <div
            className={'w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold'}
            style={{background: 'linear-gradient(135deg, #1a3550)'}}
          >
            +
          </div>
          <div>
            <div className={'font-bold text-gray-800 text-base'}>Add New Product</div>
            <div className={'text-gray-400 font-normal text-sm'}>Fill in all product details below</div>
          </div>
        </div>
      )}
      open={isModalVisible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={'Save Product'}
      okButtonProps={{className: 'btn-primary px-6', loading: isLoading}}
      cancelButtonProps={{className: 'rounded-md'}}
      width={600}
      destroyOnHidden
      styles={{
        header: {borderBottom: `3px solid ${PRIMARY_LIGHT}`, paddingBottom: 16},
        body:   {padding: '20px 24px'},
      }}
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={onFinish}
        initialValues={{status: 'ACTIVE', quantity: 0, cgst: 0, sgst: 0, igst: 0}}
      >
        {/* ── Basic Information ── */}
        <div className={'rounded-md p-4 mb-4 bg-primary-50 border border-primary-100'}>
          <SectionTitle>Basic Information</SectionTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={'productName'}
                label={<span className={'font-semibold'}>Product Name</span>}
                rules={[{required: true, message: 'Product name is required'}]}
              >
                <Input className={'rounded-md'} placeholder={'e.g. Haldi Powder'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={'eanCode'}
                label={<span className={'font-semibold'}>EAN Code</span>}
                rules={[
                  {required: true, message: 'EAN Code is required'},
                  {
                    pattern: /^\d+$/,
                    message: 'EAN Code must contain only numbers',
                  },
                ]}
              >
                <Input
                  className={'rounded-md'}
                  placeholder={'e.g. 1234567890123'}
                  maxLength={13}
                  onChange={e => {
                    const value = e.target.value.replace(/\D/g, '');
                    e.target.value = value;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={'status'}
                label={<span className={'font-semibold'}>Status</span>}
                rules={[{required: true, message: 'Status is required'}]}
              >
                <Select
                  options={[
                    {label: '✅ Active',   value: 'ACTIVE'},
                    {label: '❌ Inactive', value: 'INACTIVE'},
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={'quantity'}
                label={<span className={'font-semibold'}>Quantity</span>}
                rules={[
                  {required: true, message: 'Quantity is required'},
                  {type: 'number', min: 0, message: 'Must be a positive number'},
                ]}
              >
                <InputNumber min={0} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* ── Tax Configuration ── */}
        <div className={'rounded-md p-4 bg-primary-50 border border-primary-100'}>
          <SectionTitle>Tax Configuration</SectionTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={'cgst'}
                label={<span className={'font-semibold'}>CGST (%)</span>}
              >
                <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={'sgst'}
                label={<span className={'font-semibold'}>SGST (%)</span>}
              >
                <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name={'igst'}
                label={<span className={'font-semibold'}>IGST (%)</span>}
              >
                <InputNumber 
                  disabled
                  min={0} 
                  max={100} 
                  step={0.01} 
                  className={'w-full rounded-md'} 
                  placeholder={'0'} 
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      </Form>
    </Modal>
  );
};

AddProductScreen.propTypes = {
  isModalVisible:   PropTypes.bool.isRequired,
  handleCancel:     PropTypes.func.isRequired,
  form:             PropTypes.object.isRequired,
  token:            PropTypes.string.isRequired,
  onProductCreated: PropTypes.func.isRequired,
};

export default AddProductScreen;
